// components/ui/LoadingSpinner.tsx
import React from 'react'

interface LoadingSpinnerProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  color?: string
  className?: string
  fullScreen?: boolean
  text?: string
  showText?: boolean
  overlay?: boolean
}

const sizeClasses = {
  xs: 'w-4 h-4 border-2',
  sm: 'w-6 h-6 border-2',
  md: 'w-8 h-8 border-3',
  lg: 'w-12 h-12 border-4',
  xl: 'w-16 h-16 border-4'
}

const textSizeClasses = {
  xs: 'text-xs',
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl'
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  color = 'text-blue-500',
  className = '',
  fullScreen = false,
  text = 'Loading...',
  showText = false,
  overlay = false
}) => {
  const spinner = (
    <div className={`inline-block ${sizeClasses[size]} ${className}`}>
      <div className={`
        w-full h-full rounded-full border-solid border-t-transparent border-r-transparent border-b-transparent
        animate-spin ${color}
      `} />
    </div>
  )

  if (fullScreen) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-gray-900/90 backdrop-blur-sm">
        {spinner}
        {showText && (
          <div className={`mt-4 ${textSizeClasses[size]} text-gray-300`}>
            {text}
          </div>
        )}
      </div>
    )
  }

  if (overlay) {
    return (
      <div className="absolute inset-0 z-40 flex flex-col items-center justify-center bg-gray-900/50 backdrop-blur-sm rounded-lg">
        {spinner}
        {showText && (
          <div className={`mt-4 ${textSizeClasses[size]} text-gray-300`}>
            {text}
          </div>
        )}
      </div>
    )
  }

  if (showText) {
    return (
      <div className="flex flex-col items-center justify-center">
        {spinner}
        <div className={`mt-2 ${textSizeClasses[size]} text-gray-400`}>
          {text}
        </div>
      </div>
    )
  }

  return spinner
}

// Dots loading animation
export const LoadingDots: React.FC<{
  size?: 'sm' | 'md' | 'lg'
  color?: string
  className?: string
}> = ({
  size = 'md',
  color = 'bg-gray-400',
  className = ''
}) => {
  const dotSize = {
    sm: 'w-1.5 h-1.5',
    md: 'w-2 h-2',
    lg: 'w-3 h-3'
  }

  return (
    <div className={`flex items-center space-x-1 ${className}`}>
      <div className={`${dotSize[size]} ${color} rounded-full animate-pulse`} style={{ animationDelay: '0ms' }} />
      <div className={`${dotSize[size]} ${color} rounded-full animate-pulse`} style={{ animationDelay: '150ms' }} />
      <div className={`${dotSize[size]} ${color} rounded-full animate-pulse`} style={{ animationDelay: '300ms' }} />
    </div>
  )
}

// Skeleton loader
export const SkeletonLoader: React.FC<{
  type?: 'text' | 'circle' | 'rectangle'
  width?: string
  height?: string
  className?: string
  count?: number
}> = ({
  type = 'text',
  width = '100%',
  height = '1rem',
  className = '',
  count = 1
}) => {
  const baseClasses = 'animate-pulse bg-gray-700 rounded'

  const typeClasses = {
    text: '',
    circle: 'rounded-full',
    rectangle: 'rounded-lg'
  }

  const elements = Array.from({ length: count }, (_, i) => (
    <div
      key={i}
      className={`${baseClasses} ${typeClasses[type]} ${className}`}
      style={{ width, height, marginBottom: count > 1 && i < count - 1 ? '0.5rem' : '0' }}
    />
  ))

  return <>{elements}</>
}

// Content loader with skeleton
export const ContentLoader: React.FC<{
  lines?: number
  className?: string
}> = ({
  lines = 3,
  className = ''
}) => {
  return (
    <div className={`space-y-3 ${className}`}>
      <SkeletonLoader width="100%" height="1.5rem" />
      {Array.from({ length: lines - 1 }).map((_, i) => (
        <SkeletonLoader key={i} width={`${100 - i * 10}%`} height="1rem" />
      ))}
    </div>
  )
}